import { useEffect, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import CloudIcon from '@material-ui/icons/Cloud';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * Knative System 选项
 */
interface KnativeSystemOption {
  name: string;
  description: string;
  owner: string;
  environments: string[]; // 已配置的环境列表
}

/**
 * KnativeSystemPicker
 *
 * 自定义字段：从 Backstage Catalog 选择 Knative 类型的 System
 * 仅显示 hose.io/powered-by=knative 的 System
 *
 * 使用方式：
 * ```yaml
 * system:
 *   title: 所属系统
 *   type: string
 *   ui:field: KnativeSystemPicker
 * ```
 */
interface KnativeSystemPickerProps {
  onChange: (value: string | undefined) => void;
  rawErrors?: string[];
  required?: boolean;
  formData?: string;
  idSchema?: { $id: string };
  schema?: { title?: string; description?: string };
}

export const KnativeSystemPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  idSchema,
  schema,
}: KnativeSystemPickerProps) => {
  const catalogApi = useApi(catalogApiRef);

  const [systems, setSystems] = useState<KnativeSystemOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const loadSystems = async () => {
      try {
        setLoading(true);
        setError(undefined);

        // 从 Catalog 获取所有 System 实体
        const response = await catalogApi.getEntities({
          filter: {
            kind: 'System',
          },
        });

        // 过滤出 Knative 类型的 System
        const knativeSystems = response.items.filter(entity => {
          const poweredBy =
            entity.metadata.annotations?.['hose.io/powered-by'];
          return poweredBy === 'knative';
        });

        const systemList: KnativeSystemOption[] = knativeSystems.map(entity => {
          const annotations = entity.metadata.annotations || {};
          const environments: string[] = [];

          // 检查已配置的环境
          if (annotations['hose.io/knative-dest-test']) {
            environments.push('TEST');
          }
          if (annotations['hose.io/knative-dest-staging']) {
            environments.push('STAGING');
          }
          if (annotations['hose.io/knative-dest-prod']) {
            environments.push('PROD');
          }

          return {
            name: entity.metadata.name,
            description: entity.metadata.description || '',
            owner: (entity.spec?.owner as string) || '',
            environments,
          };
        });

        // 按名称排序
        systemList.sort((a, b) => a.name.localeCompare(b.name));

        setSystems(systemList);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load Knative systems',
        );
      } finally {
        setLoading(false);
      }
    };

    loadSystems();
  }, [catalogApi]);

  const fieldId = idSchema?.$id || 'knative-system-picker';

  return (
    <FormControl
      margin="normal"
      required={required}
      error={(rawErrors?.length ?? 0) > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={fieldId}>
        {schema?.title || 'Knative System'}
      </InputLabel>
      <Select
        id={fieldId}
        value={formData || ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading}
      >
        {loading && (
          <MenuItem value="" disabled>
            加载中...
          </MenuItem>
        )}
        {error && (
          <MenuItem value="" disabled>
            加载失败: {error}
          </MenuItem>
        )}
        {!loading && !error && systems.length === 0 && (
          <MenuItem value="" disabled>
            无可用 Knative System（请先创建）
          </MenuItem>
        )}
        {systems.map(system => (
          <MenuItem key={system.name} value={system.name}>
            <Box display="flex" alignItems="center" width="100%">
              <CloudIcon
                fontSize="small"
                style={{ marginRight: 8, color: '#4a90d9' }}
              />
              <Box flexGrow={1}>
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  {system.name}
                </Typography>
                {system.description && (
                  <Typography variant="caption" color="textSecondary">
                    {system.description}
                  </Typography>
                )}
              </Box>
              <Box>
                {system.environments.map(env => (
                  <Chip
                    key={env}
                    label={env}
                    size="small"
                    style={{ marginLeft: 4 }}
                  />
                ))}
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {(rawErrors?.length ?? 0) > 0 && !formData && (
        <FormHelperText error>此字段为必填项</FormHelperText>
      )}
      {formData &&
        (() => {
          const selected = systems.find(s => s.name === formData);
          return selected ? (
            <FormHelperText>
              已选择: {selected.name}
              {selected.environments.length > 0 &&
                ` (环境: ${selected.environments.join(', ')})`}
            </FormHelperText>
          ) : null;
        })()}
    </FormControl>
  );
};

